import React, { FC } from 'react';
import {
  Position, Intent, Toaster, ToasterPosition,
} from '@blueprintjs/core';
import heic2any from 'heic2any';
import { DOCTOR_PREFIX_LIST, allStatus } from '../constants';

export const AppToaster = (windowWidth: number) => Toaster.create({
  className: 'radical-health-toaster',
  position: windowWidth > 1000 ? Position.TOP_RIGHT : Position.BOTTOM,
});

export const getTitle = (value: string) => {
  if (!value) return '';
  const firstCharacter = value[0]?.toLocaleUpperCase();
  return firstCharacter + value.slice(1).toLowerCase();
};

export const ToastContainer = (position: ToasterPosition) => Toaster.create({
  position,
});

export const convertBytesToMegaBytes = (fileSize: number) => Number((fileSize / (1024 * 1024)).toFixed(2));

// Convert Data Url to File Object
export const url2File = async (url: string, fileType: string) => {
  const blob = await (await fetch(url)).blob();
  return new File([blob], `Profile_Picture_${new Date().valueOf()}.jpg`, {
    type: blob.type,
  });
};

// Convert File Object to Data Url
export const convert2DataUrl = (blobOrFile: File) => URL.createObjectURL(blobOrFile);

// Trim file name to a certain length without file extension
export const getTrimmedFileName = (fileName: string, index?: number) => fileName?.split('.')[0].slice(0, index ?? fileName?.length);

export const getInitials = (name: string) => {
  let initials = '-';
  const nameSplit = name.split(' ');
  const nameLength = nameSplit.length;
  if (nameLength > 1) {
    initials = nameSplit[0].substring(0, 1) + nameSplit[nameLength - 1].substring(0, 1);
  } else if (nameLength === 1) {
    initials = nameSplit[0].substring(0, 1);
  } else return undefined;

  return initials.toUpperCase();
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i += 1) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const createImageFromInitials = (
  size: number,
  name: string,
  colorCode?: string,
) => {
  const color = colorCode ?? getRandomColor();
  const shortName = getInitials(name || '-');

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = size;
  canvas.height = size;

  if (context) {
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);

    context.fillStyle = `${color}50`;
    context.fillRect(0, 0, size, size);

    context.fillStyle = color;
    context.textBaseline = 'middle';
    context.textAlign = 'center';
    context.font = `${size / 2}px Roboto`;
    context.fillText(shortName ?? '-', size / 2, size / 2);
  }

  return canvas.toDataURL();
};

export const getTitleCaseFromSnakeCase = (str: string) => str
  && str
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatDate = (date: Date | null, dateFormat?: string) => {
  if (!date) return '';
  let formattedDate = dateFormat;

  const month = date.getMonth() ? date.getMonth() + 1 : 'MM';
  const day = date.getDate() ? date.getDate() : 'dd';
  const year = date.getFullYear() ? date.getFullYear() : 'yyyy';

  formattedDate = formattedDate?.replace('MM', month.toString());
  formattedDate = formattedDate?.replace('dd', day.toString());
  formattedDate = formattedDate?.replace('yyyy', year.toString());

  if (!dateFormat) return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  return formattedDate!;
};

export const convertToTitleCaseFromCamelCase = (str: string) => {
  const text = str;
  const result = text.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const convertObjectKeysFromSnakeToCamelCase = (data: any) => {
  const convert = (str: string) => str.replace(/(?!^)_(.)/g, (_, char) => char.toUpperCase());

  const result: any = {};
  Object.keys(data).forEach((key) => {
    let convertedValue = data[key];
    if (data[key] === null) {
      result[convert(key)] = convertedValue;
    } else if (Array.isArray(data[key])) {
      if (
        typeof data[key][0] === 'object'
        && Object.keys(data[key][0]).length > 0
      ) {
        convertedValue = data[key].map((item: any) => convertObjectKeysFromSnakeToCamelCase(item));
      }
      result[convert(key)] = convertedValue;
    } else if (
      typeof data[key] === 'object'
      && Object.keys(data[key]).length > 0
    ) {
      convertedValue = convertObjectKeysFromSnakeToCamelCase(data[key]);
      result[convert(key)] = convertedValue;
    } else {
      result[convert(key)] = convertedValue;
    }
  });
  return result;
};

export const convertObjectKeysFromCamelToSnakeCase = (data: any) => {
  const convert = (str: string) => str.replace(/[A-Z0-9]/g, (letter) => `_${letter.toLowerCase()}`);

  const result: any = {};
  Object.keys(data).forEach((key) => {
    let convertedValue = data[key];
    if (data[key] === null) {
      result[convert(key)] = convertedValue;
    } else if (Array.isArray(data[key])) {
      if (
        typeof data[key][0] === 'object'
        && Object.keys(data[key][0]).length > 0
      ) {
        convertedValue = data[key].map((item: any) => convertObjectKeysFromCamelToSnakeCase(item));
      }
      result[convert(key)] = convertedValue;
    } else if (
      typeof data[key] === 'object'
      && Object.keys(data[key]).length > 0
    ) {
      convertedValue = convertObjectKeysFromCamelToSnakeCase(data[key]);
      result[convert(key)] = convertedValue;
    } else {
      result[convert(key)] = convertedValue;
    }
  });
  return result;
};

export const getDoctorInitials = (name: string) => {
  const doctorInitialsArray = ['Dr. ', 'Dr ', 'Doctor ', 'Dr, ', 'Doc '];
  const doctorInitials = doctorInitialsArray.find((initial) => name?.includes(initial));
  const isInitialsAvailable = name?.startsWith(doctorInitials!);

  if (isInitialsAvailable) {
    return doctorInitials;
  }
  return 'Dr. ';
};

export const getLocalItem = (key: string) => localStorage.getItem(key);

export const getItemFromClientStore = (key: string) => localStorage.getItem(key) ?? '';

export const getLocalDateFormat = (date: Date) => {
  if (date) {
    const localTimeFormat = new Date(date);
    const formattedDate = localTimeFormat.toLocaleDateString();
    return formattedDate;
  }
  return 'Not available';
};

export const fileToBlob = async (file: File): Promise<Blob> => new Promise<Blob>((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const blob = new Blob([reader.result!], { type: file.type });
    resolve(blob);
  };
  reader.onerror = reject;
  reader.readAsArrayBuffer(file);
});

export const convertHEICToJPEG = async (file: File) => {
  if (file) {
    try {
      const blob = await fileToBlob(file);
      const picture = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 0.3,
      });
      // eslint-disable-next-line no-undef
      return new File([picture as BlobPart], `${file.name.split('.')[0]}.jpg`);
    } catch (error) {
      console.error('Error converting file to blob:', error);
      return file;
    }
  }
  return undefined;
};

export const getFullNameWithPrefix = (value: string) => {
  const prefix = getDoctorInitials(value);

  if (value?.length === 0) {
    return `${prefix}-`;
  }
  const isPrefixExist = DOCTOR_PREFIX_LIST.includes(
    value?.split(' ')[0]?.trim(),
  );

  if (isPrefixExist) {
    return value || '-';
  }
  return `${prefix}${value || '-'}`;
};
export const getApplicantDetailsUrl = (status: string, id: string) => `/admin/${status === allStatus.MEMBER ? 'members' : 'applicants'}/${id}`;

export const renderShellAsDraft = (status: string) => (status === allStatus.SHELL_APPLICATION ? allStatus.DRAFT_APPLICATION : status);

export const setItemInClientStore = (
  key: string | string[],
  value: string | string[],
) => {
  if (Array.isArray(key) && Array.isArray(value)) {
    key.forEach((keyItem: string, index: number) => {
      localStorage.setItem(keyItem, value[index]);
    });
  } else if (typeof key === 'string' && typeof value === 'string') {
    localStorage.setItem(key, value);
  }
};

export const getSuccessResponseStatus = (status: number) => status >= 200 && status < 300;

export const convertGroupDetailResponseToForm = (data: any) => ({
  name: data.name,
  groupType: data.group_type,
  tenureStart: data.tenure_start,
  tenureEnd: data.tenure_end,
  description: data.description,
  coverPic: {
    url: data.cover_picture.file_url,
    progress: null,
    id: data.cover_picture.id,
  },
  membersCount: data.members_count,
  members: [],
  id: data.id,
});
